import { apiErrorHandler } from './errors';
import {
  createAiVisibilityApi,
  createAuthApi,
  createCapabilitiesApi,
  createCitationBuilderApi,
  createCoreApi,
  createCustomerApi,
  createGBPAReportApi,
  createGooglePlacesApi,
  createInsightsApi,
  createLocationSummaryApi,
  createLocationsApi,
  createLSGReportApi,
  createCTReportApi,
  createRMReportApi,
  createLRTReportApi,
  createSuggestionsApi,
  createUserApi,
  createWebsitePerformanceApi
} from './features';
import { createTransport } from './transport';
import { type ApiClientConfig, type DataApiConfig, type GooglePlacesApiConfig } from './types';

const buildHeaders = (token: string | null, defaultHeaders?: Record<string, string>) => ({
  ...defaultHeaders,
  ...(token ? { Authorization: `Bearer ${token}` } : {})
});

export const createApiClient = (
  config: ApiClientConfig,
  googlePlacesConfig: GooglePlacesApiConfig,
  dataApiConfig: DataApiConfig
) => {
  // Internal API transport with Bearer token authentication
  const baseTransport = createTransport({
    baseUrl: config.baseUrl,
    getDefaultHeaders: () => {
      const token = config.getToken();

      return buildHeaders(token, config.defaultHeaders);
    },
    apiErrorHandler
  });

  // Wrap with middleware for authenticated endpoints
  const authedBaseTransport = config.middleware?.authMiddleware
    ? config.middleware.authMiddleware(baseTransport)
    : baseTransport;

  // BrightLocal Data API transport with Bearer token authentication
  const dataApiTransport = createTransport({
    baseUrl: dataApiConfig.baseUrl,
    getDefaultHeaders: () => {
      const token = dataApiConfig.getToken();

      return buildHeaders(token);
    },
    apiErrorHandler
  });

  // Wrap with middleware for authenticated endpoints
  const authedDataApiTransport = dataApiConfig.middleware?.authMiddleware
    ? dataApiConfig.middleware.authMiddleware(dataApiTransport)
    : dataApiTransport;

  // Google Places API transport with API key authentication
  const googlePlacesTransport = createTransport({
    baseUrl: googlePlacesConfig.baseUrl,
    getDefaultHeaders: () => ({
      'x-Goog-Api-Key': googlePlacesConfig.apiKey
    })
  });

  return {
    aiVisibility: createAiVisibilityApi(authedBaseTransport),
    auth: createAuthApi(baseTransport),
    capabilities: createCapabilitiesApi(authedBaseTransport),
    citationBuilder: createCitationBuilderApi(authedBaseTransport),
    core: createCoreApi(authedBaseTransport),
    locations: createLocationsApi(authedBaseTransport),
    lsg: createLSGReportApi(authedBaseTransport),
    ct: createCTReportApi(authedBaseTransport),
    rm: createRMReportApi(authedBaseTransport),
    lrt: createLRTReportApi(authedBaseTransport),
    gbpa: createGBPAReportApi(authedBaseTransport),
    insights: createInsightsApi(authedBaseTransport),
    locationSummary: createLocationSummaryApi(authedBaseTransport),
    user: createUserApi(authedBaseTransport),
    customer: createCustomerApi(authedBaseTransport),
    websitePerformance: createWebsitePerformanceApi(authedBaseTransport),
    suggestions: createSuggestionsApi(authedDataApiTransport),
    googlePlaces: createGooglePlacesApi(googlePlacesTransport)
  };
};

export type ApiClient = ReturnType<typeof createApiClient>;

export const apiClient = createApiClient;
